import { invoke } from '@tauri-apps/api/core';
import { listen } from '@tauri-apps/api/event';
import {
  isPermissionGranted,
  requestPermission,
  sendNotification,
} from '@tauri-apps/plugin-notification';
import type {
  AutostartState,
  HotkeyInfo,
  HotkeyOverrides,
  OverlayMode,
  Platform,
  TokenStore,
  Tokens,
} from './types';

/** Emitted by the Rust shell whenever a mode is shown (src-tauri/src/modes.rs). */
const MODE_EVENT = 'vision360://mode';

function parseTokens(raw: string): Tokens | null {
  try {
    const value: unknown = JSON.parse(raw);
    if (
      typeof value === 'object' &&
      value !== null &&
      'accessToken' in value &&
      'expiresAt' in value &&
      typeof value.accessToken === 'string' &&
      typeof value.expiresAt === 'number'
    ) {
      const refresh = 'refreshToken' in value ? value.refreshToken : undefined;
      return {
        accessToken: value.accessToken,
        expiresAt: value.expiresAt,
        ...(typeof refresh === 'string' ? { refreshToken: refresh } : {}),
      };
    }
  } catch {
    // Corrupt entry: treat as signed out.
  }
  return null;
}

/**
 * Desktop tokens live in the OS keychain through the Rust `tokens_*`
 * commands (PRD VS-002, ADR-0003), which validate the payload before storing
 * it. Reads are cached in memory so each API call does not cross the IPC
 * boundary; writes go straight through.
 */
function createKeychainTokenStore(): TokenStore {
  let cache: Tokens | null | undefined;
  return {
    async get() {
      if (cache === undefined) {
        const raw = await invoke<string | null>('tokens_get');
        cache = raw ? parseTokens(raw) : null;
      }
      return cache;
    },
    async set(tokens) {
      await invoke('tokens_set', { value: JSON.stringify(tokens) });
      cache = tokens;
    },
    async clear() {
      cache = null;
      await invoke('tokens_clear');
    },
  };
}

/** The Tauri implementation of TechSpec §8.2. */
export function createDesktopPlatform(): Platform {
  return {
    kind: 'desktop',
    tokens: createKeychainTokenStore(),
    setMode: (mode) => invoke('show_mode', { mode }),
    hotkeys: () => invoke<HotkeyInfo>('hotkey_info'),
    setHotkeys: (overrides: HotkeyOverrides) =>
      invoke<HotkeyInfo>('set_hotkeys', { bindings: overrides }),
    dismiss: () => invoke('hide_current'),
    autostart: {
      get: () => invoke<AutostartState>('get_autostart'),
      set: (enabled) => invoke('set_autostart', { enabled }),
    },
    // Step 32 desktop channel. A stream message never prompts: asking is the settings page's job.
    async notify({ title, body }) {
      if (await isPermissionGranted()) sendNotification(body ? { title, body } : { title });
    },
    // The plugin only answers granted or not; "not yet" lets the settings page offer to ask.
    notificationPermission: async () => ((await isPermissionGranted()) ? 'granted' : 'default'),
    requestNotificationPermission: () => requestPermission(),
    onHotkey(callback) {
      let unlisten: (() => void) | undefined;
      let cancelled = false;
      void listen<OverlayMode>(MODE_EVENT, (event) => callback(event.payload)).then((fn) => {
        if (cancelled) fn();
        else unlisten = fn;
      });
      return () => {
        cancelled = true;
        unlisten?.();
      };
    },
  };
}
